"use client"; // This is a client component

import React from "react";
import { Spinner } from "@heroui/react";
import { motion, Variants } from "motion/react";

interface LoadingSpinnerProps {
  label?: string;
  className?: string;
}

export default function LoadingSpinner({ label, className = "min-h-screen" }: LoadingSpinnerProps) {
  // Container rotation for the logo
  const logoRotationVariants: Variants = {
    animate: {
      rotate: 360,
      transition: {
        duration: 25,
        repeat: Infinity,
        ease: "linear" as const,
      },
    },
  };

  // Entry and hover/pulse variants for individual geometric paths
  const topDiamondVariants: Variants = {
    initial: { y: -30, opacity: 0, scale: 0.8 },
    animate: { 
      y: 0, 
      opacity: 1, 
      scale: 1,
      transition: {
        type: "spring" as const,
        stiffness: 120,
        damping: 12,
        delay: 0.1
      }
    }
  };

  const leftDiamondVariants: Variants = {
    initial: { x: -30, opacity: 0, scale: 0.8 },
    animate: { 
      x: 0, 
      opacity: 1, 
      scale: 1,
      transition: {
        type: "spring" as const,
        stiffness: 120,
        damping: 12,
        delay: 0.2
      }
    }
  };

  const rightDiamondVariants: Variants = {
    initial: { x: 30, opacity: 0, scale: 0.8 },
    animate: { 
      x: 0, 
      opacity: 1, 
      scale: 1,
      transition: {
        type: "spring" as const,
        stiffness: 120,
        damping: 12,
        delay: 0.3
      }
    }
  };

  const bottomDiamondVariants: Variants = {
    initial: { y: 30, opacity: 0, scale: 0.8 },
    animate: { 
      y: 0, 
      opacity: 1, 
      scale: 1,
      transition: {
        type: "spring" as const,
        stiffness: 120,
        damping: 12,
        delay: 0.4
      }
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center gap-6 ${className} select-none`}>
      {/* Decorative Outer Tech Rings & Logo Wrapper */}
      <div className="relative flex items-center justify-center w-28 h-28">
        
        {/* Ambient Pulsing Glow Backdrop */}
        <motion.div
          className="absolute w-20 h-20 rounded-full bg-blue-500/20 dark:bg-blue-600/15 blur-xl pointer-events-none"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Outer Tech Ring 1 (Thin, clockwise rotation) */}
        <motion.div
          className="absolute inset-0 rounded-full border border-indigo-500/10 dark:border-indigo-400/10 pointer-events-none"
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 20, ease: "linear" }}
        />

        {/* Outer Tech Ring 2 (Dashed, counter-clockwise rotation) */}
        <motion.div
          className="absolute -inset-2 rounded-full border border-dashed border-blue-500/20 dark:border-blue-400/20 pointer-events-none"
          animate={{ rotate: -360 }}
          transition={{ repeat: Infinity, duration: 15, ease: "linear" }}
        />

        {/* HeroUI Spinner overlay (High tech HUD arc effect) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none scale-[1.3]">
          <Spinner 
            color="accent" 
            size="lg"
            className="text-indigo-500 dark:text-indigo-400"
          />
        </div>

        {/* Brand Logo Container (Slow rotating layout) */}
        <motion.div
          className="relative w-12 h-12 z-10 drop-shadow-[0_4px_12px_rgba(0,119,204,0.15)]"
          variants={logoRotationVariants}
          animate="animate"
        >
          <svg
            viewBox="0 0 1080 1080"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full overflow-visible"
          >
            {/* White Circular Backdrop for Logo Contrast */}
            <motion.circle
              cx="540"
              cy="540"
              r="540"
              fill="white"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 100, damping: 12 }}
            />
            
            {/* Top Diamond - Medium Blue */}
            <motion.path
              d="M539.5 541.294L320.353 322.147L504.145 138.355C523.671 118.829 555.329 118.829 574.855 138.355L758.647 322.147L539.5 541.294Z"
              fill="#0077CC"
              variants={topDiamondVariants}
              initial="initial"
              animate="animate"
            />
            <motion.path
              d="M539.5 541.294L320.353 322.147L504.145 138.355C523.671 118.829 555.329 118.829 574.855 138.355L758.647 322.147L539.5 541.294Z"
              stroke="rgba(0,0,0,0.1)"
              strokeWidth="10"
              variants={topDiamondVariants}
              initial="initial"
              animate="animate"
            />
            
            {/* Left Diamond - Dark Teal-Blue */}
            <motion.path
              d="M321.147 759.647L137.355 575.856C117.829 556.33 117.829 524.671 137.355 505.145L321.147 321.353L540.294 540.5L321.147 759.647Z"
              fill="#012C3C"
              variants={leftDiamondVariants}
              initial="initial"
              animate="animate"
            />
            <motion.path
              d="M321.147 759.647L137.355 575.856C117.829 556.33 117.829 524.671 137.355 505.145L321.147 321.353L540.294 540.5L321.147 759.647Z"
              stroke="rgba(0,0,0,0.1)"
              strokeWidth="10"
              variants={leftDiamondVariants}
              initial="initial"
              animate="animate"
            />
            
            {/* Right Diamond - Sky Blue */}
            <motion.path
              d="M941.645 505.145C961.171 524.671 961.171 556.33 941.645 575.856L757.853 759.647L538.706 540.5L757.853 321.353L941.645 505.145Z"
              fill="#0288E8"
              variants={rightDiamondVariants}
              initial="initial"
              animate="animate"
            />
            <motion.path
              d="M941.645 505.145C961.171 524.671 961.171 556.33 941.645 575.856L757.853 759.647L538.706 540.5L757.853 321.353L941.645 505.145Z"
              stroke="rgba(0,0,0,0.1)"
              strokeWidth="10"
              variants={rightDiamondVariants}
              initial="initial"
              animate="animate"
            />
            
            {/* Bottom Diamond - Light Blue */}
            <motion.path
              d="M539.5 539.706L758.647 758.853L574.855 942.645C555.329 962.171 523.671 962.171 504.145 942.645L320.353 758.853L539.5 539.706Z"
              fill="#129CFF"
              variants={bottomDiamondVariants}
              initial="initial"
              animate="animate"
            />
          </svg>
        </motion.div>
      </div>

      {/* Animated Label */}
      {label && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
          className="text-xs font-semibold text-neutral-500 dark:text-zinc-400 uppercase tracking-widest font-outfit"
        >
          {label}
        </motion.div>
      )}
    </div>
  );
}

